import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { DEAL_VIEW } from "@/lib/constants";
import { fmtDate, fmtDateTime, fmtRupiah } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer } from "lucide-react";

const PRINT_CSS = "@media print { body * { visibility: hidden; } #spk-doc, #spk-doc * { visibility: visible; } #spk-doc { position: absolute; left: 0; top: 0; width: 100%; border: 0; box-shadow: none; } }";

function Field({ label, value }) {
  return <div className="flex justify-between gap-3 text-sm border-b border-dashed py-1"><span className="text-slate-600">{label}</span><span className="font-medium text-right">{value || "-"}</span></div>;
}

export default function SpkPrint() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDeal = user && DEAL_VIEW.includes(user.role);
  const version = params.get("v") === "customer" && canDeal ? "customer" : "internal";
  const [p, setP] = useState(null);
  const [deal, setDeal] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then(({ data }) => setP(data)).catch(() => { toast.error("Proyek tidak ditemukan"); navigate("/projects"); });
    if (version === "customer") api.get(`/projects/${id}/deal`).then(({ data }) => setDeal(data)).catch(() => {});
  }, [id, version, navigate]);

  if (!p) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const approvedAdds = (deal?.addendums || []).filter((a) => a.status === "Disetujui");

  return (
    <div className="space-y-3" data-testid="spk-print-page">
      <style>{PRINT_CSS}</style>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="spk-back-btn"><ArrowLeft size={15} className="mr-1.5" />Kembali</Button>
        <Button onClick={() => window.print()} className="bg-primary hover:bg-red-700" data-testid="spk-print-btn"><Printer size={15} className="mr-1.5" />Cetak / PDF</Button>
      </div>
      <Card id="spk-doc" className="p-6 max-w-3xl mx-auto bg-white" data-testid="spk-doc">
        <div className="flex items-start justify-between border-b-2 border-primary pb-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center font-heading font-extrabold text-white text-xl">K3</div>
            <div><div className="font-heading font-bold text-navy text-lg">K3 REPAIR SYSTEM</div><div className="text-xs text-slate-600">Karoseri & Perbaikan Kendaraan Niaga</div></div>
          </div>
          <div className="text-right">
            <div className="font-heading font-bold text-navy">SURAT PERINTAH KERJA</div>
            <div className="font-mono-k3 text-sm text-primary" data-testid="spk-ro-number">{p.ro_number || "Belum terbit"}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wide mt-1 text-slate-600" data-testid="spk-version">{version === "customer" ? "Versi Customer / Finance" : "Versi Internal Produksi"}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 mb-4">
          <div>
            <Field label="Project ID" value={p.project_id} />
            <Field label="Customer" value={p.customer_name} />
            <Field label="PIC Customer" value={p.pic_customer} />
            <Field label="Pekerjaan" value={p.work_type} />
            <Field label="Prioritas" value={p.priority} />
          </div>
          <div>
            <Field label="No. Polisi" value={p.plate} />
            <Field label="Merek / Tipe" value={`${p.brand || ""} ${p.model || ""}`.trim()} />
            <Field label="No. Rangka" value={p.vehicle?.chassis_number} />
            <Field label="Tanggal Masuk" value={p.unit_in_at ? fmtDateTime(p.unit_in_at) : "-"} />
            <Field label="Target Selesai" value={fmtDate(p.target_date)} />
            <Field label="Penanggung Jawab" value={p.penanggung_jawab_name} />
          </div>
        </div>
        <div className="mb-4">
          <div className="text-xs font-semibold text-navy uppercase mb-1">Uraian Pekerjaan</div>
          <p className="text-sm whitespace-pre-wrap border rounded-md p-3 min-h-[70px]">{p.description || p.title || "-"}</p>
          {p.notes && <p className="text-xs text-slate-600 mt-1">Catatan khusus: {p.notes}</p>}
        </div>
        {version === "customer" && (
          <div className="mb-4" data-testid="spk-deal-section">
            <div className="text-xs font-semibold text-navy uppercase mb-1">Nilai Kontrak</div>
            {!deal?.exists ? <p className="text-sm text-slate-600">Harga Deal belum ditetapkan.</p> : (
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b"><td className="py-1.5">Harga Deal{deal.locked ? "" : " (belum dikunci)"}</td><td className="text-right font-medium" data-testid="spk-deal-amount">{fmtRupiah(deal.harga_deal)}</td></tr>
                  {approvedAdds.map((a) => <tr key={a.id} className="border-b"><td className="py-1.5">Addendum {a.no}: {a.description}</td><td className="text-right">{fmtRupiah(a.amount)}</td></tr>)}
                  <tr><td className="py-2 font-bold text-navy">Total Nilai Kontrak</td><td className="text-right font-bold text-primary" data-testid="spk-contract-total">{fmtRupiah(deal.contract_total)}</td></tr>
                  <tr><td className="py-1 text-xs text-slate-600" colSpan={2} data-testid="spk-tax-info">Status pajak: {deal.tax_mode ? `${deal.tax_mode}${deal.ppn_percent ? ` ${deal.ppn_percent}%` : ""}` : "BELUM DITENTUKAN"}</td></tr>
                </tbody>
              </table>
            )}
          </div>
        )}
        <div className="grid grid-cols-3 gap-4 mt-10 text-center text-xs">
          {(version === "customer" ? ["Customer", "Marketing", "Manager"] : ["Kepala Produksi", "Penanggung Jawab", "Manager"]).map((s) => (
            <div key={s}><div className="h-16" /><div className="border-t pt-1">{s}</div></div>
          ))}
        </div>
        <div className="text-[10px] text-slate-500 mt-6">Dicetak {fmtDateTime(new Date().toISOString())} oleh {user?.name}</div>
      </Card>
    </div>
  );
}
